'use client';

import { useState } from 'react';
import Image, { ImageProps } from 'next/image';
import { handleImageError, handleImageLoading } from '@/lib/loaders';
import SimpleImagePlaceholder from './SimpleImagePlaceholder';

interface ImageWithFallbackProps extends Omit<ImageProps, 'src' | 'onError'> {
  src: string;
  fallbackSrc?: string;
  fallbackComponent?: React.ReactNode;
}

const ImageWithFallback = ({
  src,
  fallbackSrc,
  fallbackComponent,
  alt,
  className = '',
  width = 300,
  height = 300,
  ...props
}: ImageWithFallbackProps) => {
  const [imgSrc, setImgSrc] = useState<string>(src);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Görsel yükleme olayını işle
  const handleLoad = () => {
    setLoading(false);
  };

  // Görsel hata olayını işle
  const handleError = () => {
    setLoading(false);
    setError(true);
    
    // Eğer fallbackSrc varsa ve zaten fallback'e geçmediyse, fallback görselini kullan
    if (fallbackSrc && imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
    }
  };

  // Fallback bileşeni göster (eğer varsa ve hata olduysa)
  if (error && !fallbackSrc && fallbackComponent) {
    return <>{fallbackComponent}</>;
  }

  // Fallback görsel yoksa ve hata olmuşsa, SimpleImagePlaceholder göster
  if (error && !fallbackSrc && !fallbackComponent) {
    return (
      <SimpleImagePlaceholder 
        width={typeof width === 'number' ? width : 300}
        height={typeof height === 'number' ? height : 300}
        text={alt?.substring(0, 10) || 'Görsel Bulunamadı'}
        className={className}
      />
    );
  }

  // Yükleniyorsa loading animasyonu göster, yüklenince görseli göster
  if (loading) {
    return (
      <div className="relative block h-full min-h-[208px] w-full overflow-hidden sm:min-h-0">
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-md" aria-hidden />
        <Image
          src={imgSrc}
          alt={alt || 'Görsel'}
          width={width}
          height={height}
          className={`${className} opacity-0`}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      </div>
    );
  }

  // Başarıyla yüklendiyse sadece görseli göster
  return (
    <Image
      src={imgSrc}
      alt={alt || 'Görsel'}
      width={width}
      height={height}
      className={className}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
};

export default ImageWithFallback; 